// Single source of truth for the department-based IA and role access.
// A role sees ONLY the departments listed against it; routes are gated to match.
export const DEPARTMENTS = [
  {
    key: "bd", label: "Business Development", icon: "Briefcase",
    roles: ["owner", "admin", "chief_estimator", "business_development"],
    items: [
      { to: "/today", label: "Today", testid: "nav-today", desc: "What's owed and waiting, right now" },
      { to: "/pipeline", label: "Pipeline", testid: "nav-pipeline", desc: "Every pursuit, intake to outcome" },
      { to: "/awaiting", label: "Bids Waiting", testid: "nav-awaiting", desc: "Submitted bids, ranked by age" },
      { to: "/heat-map", label: "Win Heat Map", testid: "nav-heatmap", desc: "Pursuits ranked hottest to coldest by likelihood to win" },
      { to: "/email-agent", label: "Email Agent", testid: "nav-email-agent", desc: "One inbox: replies owed, new leads from email, suggestions, screened out, fit rules, health" },
      { to: "/clients", label: "Clients", testid: "nav-clients", desc: "How each EPC actually bids out" },
    ],
  },
  {
    key: "estimating", label: "Estimating", icon: "Calculator",
    roles: ["owner", "admin", "chief_estimator", "estimator"],
    items: [
      { to: "/estimates", label: "Estimates", testid: "nav-estimates", desc: "Bids on the board to price" },
      { to: "/estimate-io", label: "Estimate I/O", testid: "nav-estimate-io", desc: "Round-trip a full estimate to/from Excel" },
      { to: "/libraries", label: "Catalog", testid: "nav-libraries", desc: "Unified catalog — line items, assemblies, equipment, crews, cost codes & fees" },
    ],
  },
  {
    key: "approvals", label: "Approvals", icon: "CheckSquare",
    roles: ["owner", "admin", "chief_estimator", "estimator", "business_development", "project_manager"],
    items: [
      { to: "/approvals", label: "My Approvals", testid: "nav-approvals", desc: "Bids waiting on your sign-off" },
    ],
  },
  {
    key: "operations", label: "Operations", icon: "HardHat",
    roles: ["owner", "admin", "chief_estimator", "project_manager"],
    items: [
      { to: "/awarded", label: "Awarded Projects", testid: "nav-awarded", desc: "Won work, handed to the field" },
    ],
  },
  {
    key: "insights", label: "Insights", icon: "BarChart3",
    roles: ["owner", "admin", "chief_estimator", "business_development"],
    items: [
      { to: "/activity", label: "Activity", testid: "nav-activity", desc: "Company-wide log — who did what, and who moved each bid to the next stage" },
      { to: "/my-work", label: "My Work", testid: "nav-mywork", desc: "Your activity log & weekly report — bids, revisions, follow-ups, leads" },
      { to: "/analytics", label: "Bid History", testid: "nav-analytics", desc: "The record, imported + live" },
      { to: "/hit-rate", label: "Hit Rate", testid: "nav-hitrate", desc: "Win rate sliced, not blended" },
      { to: "/benchmarks", label: "Benchmarks", testid: "nav-benchmarks", desc: "$/W, man-hours/MW & win rate by scope" },
      { to: "/win-trend", label: "Win Trend", testid: "nav-wintrend", desc: "How aggressively the live book is priced" },
      { to: "/reports", label: "Reports", testid: "nav-reports", desc: "Estimate summary, cost split, sensitivity & $/W benchmarks" },
    ],
  },
  {
    key: "admin", label: "Admin", icon: "Settings",
    roles: ["owner", "admin"],
    items: [
      { to: "/team", label: "Team & Roles", testid: "nav-team", desc: "People, invites, access" },
      { to: "/roles", label: "Roles & Access", testid: "nav-roles", desc: "Who can see what" },
      { to: "/import", label: "Data Import", testid: "nav-import", desc: "Bring in legacy records" },
      { to: "/company", label: "Company Settings", testid: "nav-company", desc: "Office location & company-wide defaults" },
      { to: "/branding", label: "Proposal Branding", testid: "nav-branding", desc: "Logos, colors & fonts — your proposal identity" },
      { to: "/admin/storage", label: "File Storage", testid: "nav-storage", desc: "Google Drive service account for project files", ownerOnly: true },
    ],
  },
];

// An item's `alsoRoles` grants just that page to roles outside its department.
const itemVisible = (d, i, r) => (d.roles.includes(r) || (i.alsoRoles || []).includes(r)) && (!i.ownerOnly || r === "owner");
export const deptsForRole = (role) => {
  const r = role || "estimator";
  return DEPARTMENTS.map((d) => ({ ...d, items: d.items.filter((i) => itemVisible(d, i, r)) }))
    .filter((d) => d.items.length > 0);
};
export const itemsForRole = (role) => deptsForRole(role).flatMap((d) => d.items);
export const firstPathForRole = (role) => itemsForRole(role)[0]?.to || "/today";

export const canAccessPath = (role, path) => {
  if (path.startsWith("/bids/")) return true; // bid workspace: backend enforces perms
  if (path === "/settings" || path === "/inbox" || path === "/email-agent" || path === "/messages" || path === "/how-to" || path.startsWith("/how-to/")) return true; // available to every signed-in user
  if (path.startsWith("/claude/")) return true; // Claude review downloads / upload links: backend enforces owner
  if (path.startsWith("/leads/")) return canAccessPath(role, "/pipeline"); // a lead deep link opens on the Pipeline
  return itemsForRole(role).some((i) => path === i.to || path.startsWith(i.to + "/"));
};

export const deptOfPath = (role, path) => {
  for (const d of deptsForRole(role)) {
    if (d.items.some((i) => path === i.to || path.startsWith(i.to + "/"))) return d.key;
  }
  return null;
};
